import React, { useState } from 'react'
import { useFamilies } from '../../hooks/useFamilies'
import { useToast } from '../../hooks/useToast'

export default function JoinOrCreateFamily({ user, onFamilyCreated }) {
  const [mode, setMode] = useState('create') // 'create' or 'join'
  const [familyName, setFamilyName] = useState('')
  const [familyId, setFamilyId] = useState('')
  const [loading, setLoading] = useState(false)
  
  const { createFamily, joinFamily } = useFamilies(user)
  const { showToast } = useToast()

  const handleCreateFamily = async (e) => {
    e.preventDefault()
    
    if (!familyName.trim()) {
      showToast('Please enter a family name', 'error')
      return
    }

    setLoading(true)
    try {
      await createFamily(familyName.trim())
      showToast(`Family "${familyName}" created successfully!`, 'success')
      if (onFamilyCreated) {
        await onFamilyCreated()
      }
    } catch (error) {
      console.error('Error creating family:', error)
      showToast(error.message || 'Failed to create family', 'error')
    } finally {
      setLoading(false)
    }
  }

  const handleJoinFamily = async (e) => {
    e.preventDefault()
    
    if (!familyId.trim()) {
      showToast('Please enter a Family ID', 'error')
      return
    }

    setLoading(true)
    try {
      await joinFamily(familyId.trim())
      showToast('Successfully joined the family!', 'success')
      if (onFamilyCreated) {
        await onFamilyCreated()
      }
    } catch (error) {
      console.error('Error joining family:', error)
      showToast(error.message || 'Failed to join family', 'error')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex items-center justify-center min-h-[calc(100vh-53px)] p-6">
      <div className="glass-card max-w-md w-full p-8">
        <h1 className="text-3xl font-bold text-center mb-2 bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-400 dark:to-purple-400 bg-clip-text text-transparent">
          Welcome to Kutumb
        </h1>
        <p className="text-center text-gray-600 dark:text-gray-400 mb-8">
          Create a new family tree or join an existing one
        </p>

        {/* Mode Toggle */}
        <div className="flex gap-2 mb-6 p-1 bg-gray-200/50 dark:bg-gray-700/50 rounded-lg">
          <button
            onClick={() => setMode('create')}
            className={`flex-1 py-2 px-4 rounded-md font-medium transition-all duration-200 ${
              mode === 'create'
                ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-md'
                : 'text-gray-700 dark:text-gray-300 hover:bg-white/30 dark:hover:bg-gray-600/30'
            }`}
          >
            Create Family
          </button>
          <button
            onClick={() => setMode('join')}
            className={`flex-1 py-2 px-4 rounded-md font-medium transition-all duration-200 ${
              mode === 'join'
                ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-md'
                : 'text-gray-700 dark:text-gray-300 hover:bg-white/30 dark:hover:bg-gray-600/30'
            }`}
          >
            Join Family
          </button>
        </div>

        {/* Create Family Form */}
        {mode === 'create' && (
          <form onSubmit={handleCreateFamily} className="space-y-4">
            <div>
              <label htmlFor="familyName" className="block text-sm font-medium mb-2">
                Family Name
              </label>
              <input
                id="familyName"
                type="text"
                value={familyName}
                onChange={(e) => setFamilyName(e.target.value)}
                placeholder="e.g., The Smith Family"
                className="glass-input"
                disabled={loading}
                required
              />
            </div>

            <button
              type="submit"
              disabled={loading || !familyName.trim()}
              className="glass-btn-primary w-full"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Creating...
                </span>
              ) : (
                'Create Family'
              )}
            </button>

            <p className="text-sm text-gray-600 dark:text-gray-400 text-center mt-4">
              You'll receive a Family ID that others can use to join
            </p>
          </form>
        )}

        {/* Join Family Form */}
        {mode === 'join' && (
          <form onSubmit={handleJoinFamily} className="space-y-4">
            <div>
              <label htmlFor="familyId" className="block text-sm font-medium mb-2">
                Family ID
              </label>
              <input
                id="familyId"
                type="text"
                value={familyId}
                onChange={(e) => setFamilyId(e.target.value.toUpperCase())}
                placeholder="e.g., FAM_ABC12345"
                className="glass-input font-mono"
                disabled={loading}
                required
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Ask your family admin for the Family ID
              </p>
            </div>

            <button
              type="submit"
              disabled={loading || !familyId.trim()}
              className="glass-btn-primary w-full"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Joining...
                </span>
              ) : (
                'Join Family'
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}
